/**
 * VariantsPanel — table des variantes d'une œuvre, éditable inline.
 *
 * Affichée sur `WorkDetailPage` (onglet Aperçu). Ouvre `VariantModal` pour
 * créer ou éditer. Suppression via `useConfirm` danger.
 *
 * Empty state : si l'œuvre n'a pas encore de variante, propose de créer
 * la première (ouvre le modal). Si le catalogue est vide, le modal lui-même
 * affiche un EmptyState pointant vers `/settings`.
 */

import { useState } from 'react'
import { Pencil, Plus, SquareStack, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import RadioCard from '@/components/ui/RadioCard'
import VariantPodCell from '@/components/pod/VariantPodCell'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useDeleteVariant, useDigitalFormatsPreview } from '@/hooks/useWorks'
import {
  EDITION_KIND_FR,
  WORK_VARIANT_STATUS_FR,
  type WorkVariantWithCatalogResponse,
} from '@/types/works'

import VariantModal from './VariantModal'
import VariantsBulkCreateForm from './VariantsBulkCreateForm'

import './VariantsPanel.css'

interface VariantsPanelProps {
  workId: number
  variants: WorkVariantWithCatalogResponse[]
  /**
   * Ratios width/height de tous les sources de l'œuvre (multi-source —
   * migration 015). VariantModal filtre les formats compatibles si AU
   * MOINS UN source matche dans `formatMatchingTolerance`. Vide tant
   * qu'aucun master n'a été uploadé.
   */
  workSourceRatios: number[]
  /**
   * Tolérance ±5 % du matching format ↔ source (BE source de vérité,
   * exposée via `WorkResponse.format_matching_tolerance` Pydantic).
   */
  formatMatchingTolerance: number
}

function formatPriceCents(cents: number, currency: string): string {
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency
  return `${symbol}${(cents / 100).toFixed(2)}`
}

function describeFormat(v: WorkVariantWithCatalogResponse): string {
  // Label commercial : `label_override` si renseigné, sinon dérivé du
  // format singulier (refonte POD v6.1 : 1 variante = 1 format publiable).
  if (v.label_override) return v.label_override
  const f = v.format
  if (!f) return ''
  return f.label_fr || `${f.width_cm} × ${f.height_cm} cm`
}

function describeVariant(v: WorkVariantWithCatalogResponse): string {
  // Composition dérivée (label_override OU format · support · framing).
  // Fallback final sur `printable_spec.label_cache` pour les variantes
  // `open` POD où `format_id` est NULL (le format vit dans la spec) —
  // sans ça deux variantes open distinctes affichaient le même fallback
  // "Variante #N" alors qu'elles sont reliées à des presets POD
  // différents.
  const parts: string[] = []
  const formatLabel = describeFormat(v)
  if (formatLabel) parts.push(formatLabel)
  if (v.support_label_fr) parts.push(v.support_label_fr)
  if (v.framing_label_fr) parts.push(v.framing_label_fr)
  if (parts.length > 0) return parts.join(' · ')
  if (v.printable_spec?.label_cache) return v.printable_spec.label_cache
  return ''
}

function isDigitalVariant(v: WorkVariantWithCatalogResponse): boolean {
  // Refonte POD v6.1 : le canal de vente est porté par `medium_kind`
  // (politique unique), plus dérivé des `format.kinds`.
  return v.medium_kind === 'digital'
}

/** Une variante a besoin d'un produit POD ssi `medium_kind !== 'digital'`.
 * Une variante digitale n'a pas de POD — fulfilment = téléchargement
 * direct du livrable. */
function isPodApplicable(v: WorkVariantWithCatalogResponse): boolean {
  return v.medium_kind !== 'digital'
}

function badgeVariantForStatus(
  status: WorkVariantWithCatalogResponse['status'],
): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'active':
      return 'success'
    case 'sold_out':
      return 'warning'
    case 'archived':
      return 'danger'
    default:
      return 'default'
  }
}

export default function VariantsPanel({
  workId,
  variants,
  workSourceRatios,
  formatMatchingTolerance,
}: VariantsPanelProps) {
  const { confirm } = useConfirm()
  const deleteMutation = useDeleteVariant()

  // Création — machine 3 états :
  //   null  → fermé
  //   pick  → step 0 (RadioCards mono vs bulk)
  //   mono  → VariantModal une variante
  //   bulk  → VariantsBulkCreateForm wizard N variantes
  // Pattern aligné sur PrintableSpecModal (commit bc93c41, settings POD).
  const [creationMode, setCreationMode] = useState<null | 'pick' | 'mono' | 'bulk'>(null)
  const [editing, setEditing] = useState<WorkVariantWithCatalogResponse | null>(null)

  // Preview digital — preload si au moins une variante est digitale (badge
  // "Set numérique · X fichiers" sur sa row). Refonte 2026-05-14 : la
  // variante digitale livre N fichiers résolus dynamiquement côté BE.
  const hasDigitalVariant = variants.some(isDigitalVariant)
  const digitalPreviewQuery = useDigitalFormatsPreview(workId, {
    enabled: hasDigitalVariant,
  })

  const handleDelete = async (v: WorkVariantWithCatalogResponse) => {
    const ok = await confirm({
      title: 'Supprimer cette variante ?',
      message:
        v.status === 'active'
          ? `« ${describeVariant(v) || 'Variante'} » est active. Préfère la basculer en "archivée" si elle a déjà été publiée — sinon les ventes futures ne pourront plus s'y rattacher.`
          : `Suppression dure de « ${describeVariant(v) || 'Variante'} ». Action irréversible.`,
      variant: 'danger',
      confirmText: 'Supprimer',
    })
    if (!ok) return
    deleteMutation.mutate(
      { workId, variantId: v.id },
      {
        onSuccess: () => toast.success('Variante supprimée'),
        onError: (err) => toast.error(err.detail ?? 'Suppression impossible'),
      },
    )
  }

  return (
    <section className="variants-panel">
      <header className="variants-panel-header">
        <div>
          <h3 className="ui-h4 variants-panel-title">Variantes</h3>
          <p className="ui-body ui-text-muted variants-panel-subtitle">
            Combinaisons format × encadrement × support × édition vendables sur les plateformes.
          </p>
        </div>
        <div className="variants-panel-header-actions">
          <Button variant="primary" size="sm" onClick={() => setCreationMode('pick')}>
            <Plus aria-hidden="true" size={16} />
            Nouvelle variante
          </Button>
        </div>
      </header>

      {variants.length === 0 ? (
        <div className="variants-panel-empty">
          <p>
            Aucune variante configurée. Ajoute au moins une combinaison format × support pour
            pouvoir publier sur Etsy / Saatchi.
          </p>
        </div>
      ) : (
        <div className="variants-panel-table-wrapper">
          <table className="variants-panel-table">
            <thead>
              <tr>
                <th>Composition</th>
                <th>Édition</th>
                <th>Prix</th>
                <th>Stock</th>
                <th>Statut</th>
                <th>POD</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => (
                <tr key={v.id}>
                  <td>
                    <div className="variants-panel-composition">
                      <strong>{describeVariant(v) || `Variante #${v.id}`}</strong>
                      {isDigitalVariant(v) ? (
                        <Badge variant="default" size="sm" aria-label="Variante digitale">
                          {digitalPreviewQuery.data
                            ? `Set numérique · ${digitalPreviewQuery.data.total_files} fichiers`
                            : 'Set numérique'}
                        </Badge>
                      ) : null}
                      {isPodApplicable(v) && !v.printable_spec ? (
                        <span
                          className="variants-panel-composition-missing"
                          title="Aucun preset POD lié — choisis-en un via Modifier la variante pour activer la préparation automatique."
                        >
                          Aucun preset POD lié
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    {/* Pour digital : `edition_kind='open'` est forcé par
                        le CHECK DB `chk_wv_digital_open_only` mais n'a pas
                        de sens UX (un set numérique n'a pas de notion
                        d'édition ouverte/limitée). On affiche un tiret. */}
                    {v.medium_kind === 'digital' ? (
                      <span className="ui-text-muted">—</span>
                    ) : (
                      <>
                        {EDITION_KIND_FR[v.edition_kind ?? 'open']}
                        {v.edition_size ? ` /${v.edition_size}` : ''}
                      </>
                    )}
                  </td>
                  <td className="variants-panel-numeric">
                    {/* EUR-only assumé (refonte tarifs 2026-05-15) — colonne
                        currency droppée par migration 012. POD open : le
                        prix listé Etsy est dérivé au publish via
                        `pricing_service.compute_pod_retail_price_cents`
                        (base_cost Gelato + marge + frais), pas saisi sur
                        la variante — afficher « 0 € » serait trompeur. */}
                    {v.medium_kind === 'print' && v.edition_kind === 'open' ? (
                      <span className="ui-text-muted">Calculé au publish</span>
                    ) : (
                      formatPriceCents(v.price_cents, 'EUR')
                    )}
                  </td>
                  <td className="variants-panel-numeric">
                    {v.stock_qty === null || v.stock_qty === undefined
                      ? 'À la demande'
                      : v.stock_qty}
                  </td>
                  <td>
                    <Badge variant={badgeVariantForStatus(v.status)} size="sm">
                      {WORK_VARIANT_STATUS_FR[v.status ?? 'draft']}
                    </Badge>
                  </td>
                  <td>
                    {isPodApplicable(v) ? (
                      <VariantPodCell
                        variantId={v.id}
                        variantLabel={describeVariant(v) || `Variante #${v.id}`}
                      />
                    ) : (
                      <span
                        className="ui-text-muted ui-body-sm"
                        aria-label="POD non applicable — variante 100% digitale"
                        title="Variante 100% digitale — fulfilment = téléchargement du livrable, pas de production POD"
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="variants-panel-actions">
                    <Button
                      variant="quiet"
                      size="sm"
                      onClick={() => setEditing(v)}
                      aria-label={`Modifier ${describeVariant(v)}`}
                    >
                      <Pencil aria-hidden="true" size={16} />
                    </Button>
                    <Button
                      variant="quiet"
                      size="sm"
                      onClick={() => handleDelete(v)}
                      aria-label={`Supprimer ${describeVariant(v)}`}
                    >
                      <Trash2 aria-hidden="true" size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creationMode === 'pick' ? (
        <VariantKindPicker
          onClose={() => setCreationMode(null)}
          onPick={(kind) => setCreationMode(kind)}
        />
      ) : null}

      {creationMode === 'mono' ? (
        <VariantModal
          workId={workId}
          workSourceRatios={workSourceRatios}
          formatMatchingTolerance={formatMatchingTolerance}
          mode="create"
          onClose={() => setCreationMode(null)}
          onBack={() => setCreationMode('pick')}
        />
      ) : null}

      {creationMode === 'bulk' ? (
        <VariantsBulkCreateForm
          workId={workId}
          workSourceRatios={workSourceRatios}
          formatMatchingTolerance={formatMatchingTolerance}
          onClose={() => setCreationMode(null)}
          onBack={() => setCreationMode('pick')}
        />
      ) : null}

      {editing ? (
        <VariantModal
          workId={workId}
          workSourceRatios={workSourceRatios}
          formatMatchingTolerance={formatMatchingTolerance}
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </section>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Step 0 — picker mono vs bulk
// ──────────────────────────────────────────────────────────────────────────────

function VariantKindPicker({
  onClose,
  onPick,
}: {
  onClose: () => void
  onPick: (kind: 'mono' | 'bulk') => void
}) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Nouvelle variante"
      kicker="Œuvre · Variantes"
      size="form"
    >
      <Modal.Body>
        <div className="ui-stack">
          <p className="ui-body ui-text-muted">
            Choisis la façon de créer. Tu pourras revenir à cet écran depuis le formulaire suivant.
          </p>
          <RadioCard
            selected={false}
            onSelect={() => onPick('mono')}
            icon={<Plus size={20} />}
            label="Une seule variante"
            description="Format × support × encadrement × prix pour UNE combinaison."
          />
          <RadioCard
            selected={false}
            onSelect={() => onPick('bulk')}
            icon={<SquareStack size={20} />}
            label="Plusieurs d'un coup"
            description="Wizard 5 étapes — coche N formats, prix éditable par ligne."
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="quiet" onClick={onClose}>
          Annuler
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
